import { Search } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import type { User } from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";

/**
 * Props para el componente SearchUser
 * @param {string} search - El término de búsqueda
 * @param {function} onSearchChange - Función para manejar el cambio de búsqueda
 * @param {User[]} filteredUsers - Los usuarios filtrados
 * @param {boolean} isLoadingFilteredUsers - Indica si se está cargando la búsqueda
 * @param {Error | null} errorFilteredUsers - El error de la búsqueda
 * @param {User | null} selectedUser - El usuario seleccionado
 * @param {function} onUserSelect - Función para manejar la selección de usuario
 * @param {string} title - El título del componente
 */
interface SearchUserProps {
  search: string;
  onSearchChange: (value: string) => void;
  filteredUsers: User[] | undefined;
  isLoadingFilteredUsers: boolean;
  errorFilteredUsers: Error | null;
  selectedUser: User | null;
  onUserSelect: (user: User) => void;
  debouncedSearch: string;
  title: string;
}

/**
 * Tipos de usuarios
 */
const userTypes = ["institution", "physician", "admin", "superadmin", "patient"];

/**
 * Labels para los tipos de usuarios
 */
const userTypeLabels = {
  [userTypes[0]]: "Institución",
  [userTypes[1]]: "Médico",
  [userTypes[2]]: "Administrador",
  [userTypes[3]]: "Superadministrador",
  [userTypes[4]]: "Paciente",
};

/**
 * Componente para la sección de búsqueda
 * @param {SearchSectionProps} props - Las props del componente
 * @returns {React.JSX.Element} El componente SearchSection
 */
export function SearchUser({
  search,
  title,
  onSearchChange,
  filteredUsers,
  isLoadingFilteredUsers,
  errorFilteredUsers,
  selectedUser,
  onUserSelect,
  debouncedSearch,
}: SearchUserProps): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>
          Ingresa al menos 3 caracteres para iniciar la búsqueda
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {title}
            </label>
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={`${title}...`}
              className="w-full"
            />
          </div>

          {/* Search Results */}
          {debouncedSearch.length >= 3 && (
            <div className="space-y-2">
              {isLoadingFilteredUsers ? (
                <div className="text-sm text-gray-500">Buscando...</div>
              ) : errorFilteredUsers ? (
                <div className="text-sm text-red-500">Algo salió mal :(</div>
              ) : filteredUsers && filteredUsers.length > 0 ? (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Resultados de búsqueda:
                  </label>
                  <div className="max-h-40 overflow-y-auto border rounded-md">
                    {filteredUsers.map((user) => (
                      <div
                        key={user.uid}
                        className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 border-b last:border-b-0 ${
                          selectedUser?.uid === user.uid
                            ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700"
                            : ""
                        }`}
                        onClick={() => onUserSelect(user)}
                      >
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {getUserFullName(user)}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {userTypeLabels[user.type]}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No se encontraron resultados</div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
